import {
  FlatBarcode,
  ScannerMode,
} from '@buncha/components/composites/Scanner/type';
import {HomeScreens, ShopperScreens} from '@buncha/home/navigation/types';
import {
  NSCItemScannerAction,
  useNSCBuyersDetails,
  useNSCItemDetails,
  useNSCItemScannerReducer,
} from '../hooks/NSCItemScanner';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  isNSC2,
  removeLeadingZeroes,
} from '@buncha/batchDetails/utils/ScannedAddItem';

import B2BLocationInStore from '../components/ItemDetail/B2BLocationInStore';
import {B2BScreens} from '@buncha/B2B/navigation/types';
import BottomButtons from '../components/NSCItemScanner/bottomButtons';
import {Box} from '@buncha/components/core/view';
import Header from '@buncha/components/composites/Header/Header';
import If from '@buncha/components/conditional/If';
import {ItemDetailsScreen} from '../navigation/types';
import {Modules} from '@buncha/utils/types';
import NSCItemDetails from '../components/NSCItemScanner/NSCItemDetails';
import {NSCItemScannerContext} from '../context/NSCItemScanner';
import ScreenHeader from '@buncha/common/components/screenHeader';
import {StyleSheet} from 'react-native';
import {ToastType} from '@buncha/components/composites/notification/type';
import {navigation} from '@buncha/config/navigation';
import {useGetLocale} from '@buncha/localization/hooks';
import {useRoute} from '@react-navigation/native';
import {useToastMessage} from '@buncha/components/composites/notification';
import Scanner from '@buncha/components/composites/Scanner';

const NSCItemScanner = () => {
  const [state, dispatch] = useNSCItemScannerReducer();
  const [showWarningToast] = useToastMessage(ToastType.Warning);
  const routeParams: any = useRoute()?.params;
  const catalogItemId = routeParams?.catalogItemId;
  const orderItemId = routeParams?.orderItemId;
  const runId = routeParams?.runId;
  const returnPage = routeParams?.returnPage;
  const {title} =
    useGetLocale(Modules.itemDetails, ItemDetailsScreen.NSCItemScanner) ?? {};

  const storeId = state.buyers?.[0]?.order?.run?.store?.id;

  const {buyersLoading, getNSCBuyers} = useNSCBuyersDetails(dispatch);
  const {itemLoading, getNSCItemDetails} = useNSCItemDetails(dispatch);

  const [scannerActive, setScannerActive] = useState(true);
  const [triggerAddAnother, setTriggerAddAnother] = useState<boolean>(true);
  const lastScannedBarcode = useRef<string | null>();

  const provider = useMemo(() => ({state, dispatch}), [dispatch, state]);

  const onScan = useCallback(
    (barcode: FlatBarcode) => {
      if (!barcode.data) return;
      const trimmedUpc = removeLeadingZeroes(barcode.data);
      const upc = trimmedUpc.substring(0, trimmedUpc.length - 1);
      const isCorrect = Boolean(
        state.item?.secondaryBarcode.find(preLoadedUpc => {
          return (
            upc === preLoadedUpc ||
            (!isNaN(Number(upc)) && Number(preLoadedUpc) === Number(upc)) ||
            trimmedUpc === preLoadedUpc ||
            (!isNaN(Number(trimmedUpc)) &&
              Number(preLoadedUpc) === Number(trimmedUpc))
          );
        }),
      );
      if (!isNSC2(barcode.data) && !isCorrect)
        return showWarningToast(
          'Oops! Wrong Barcode. Please print and scan this item’s barcode at the weight scale.',
        );
      lastScannedBarcode.current = removeLeadingZeroes(barcode.data);
      dispatch?.(
        NSCItemScannerAction.addBarcode(removeLeadingZeroes(barcode.data)),
      );
    },
    [dispatch, showWarningToast, state.item?.secondaryBarcode],
  );

  const removeLastScannedBarcode = useCallback(() => {
    setScannerActive(true);
    if (!lastScannedBarcode.current) return;
    dispatch?.(NSCItemScannerAction.removeBarcode(lastScannedBarcode.current));
    lastScannedBarcode.current = null;
  }, [dispatch]);

  const fetchDetails = useCallback(async () => {
    if (!catalogItemId || !orderItemId || !runId) return;
    getNSCItemDetails(catalogItemId, runId, routeParams?.storeIndex);
    if (
      returnPage === B2BScreens.B2BCheckout ||
      returnPage === ShopperScreens.OrderPackingPage
    )
      return;
    const res = await getNSCBuyers(catalogItemId, runId, orderItemId);
    if (res === 0) {
      if (returnPage === B2BScreens.B2BCheckout) return navigation.goBack();
      if (returnPage === ShopperScreens.OrderPackingPage)
        return navigation.navigate(HomeScreens.Shopper, {
          screen: ShopperScreens.OrderPackingPage,
          params: {runId: runId},
        });
      navigation.navigate(HomeScreens.Shopper, {
        screen: ShopperScreens.BatchDetailsPage,
        params: {runId: runId},
      });
    }
  }, [
    catalogItemId,
    getNSCBuyers,
    getNSCItemDetails,
    orderItemId,
    returnPage,
    routeParams?.storeIndex,
    runId,
  ]);

  const headerMenu = useMemo(() => {
    return (
      <If condition={state?.item?.itemLocation}>
        <B2BLocationInStore
          storeId={storeId}
          location={state.item?.itemLocation}
        />
      </If>
    );
  }, [state.item?.itemLocation, storeId]);

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NSCItemScannerContext.Provider value={provider}>
      <Header
        loading={(buyersLoading || itemLoading) && !state.buyers?.length}
        hideBottomSafeArea
        showDefaultLeftHeader={!state.item?.itemLocation}
        headerRightComponent={headerMenu}>
        <Box style={styles.parentBox}>
          <If condition={!state.item?.itemLocation}>
            <ScreenHeader title={title?.scanBarcode} />
          </If>
          <NSCItemDetails />
          <Scanner
            mode={ScannerMode.singleScan}
            scannerEnabled={scannerActive}
            setScannerEnabled={setScannerActive}
            onScan={onScan}
            triggerAddAnother={triggerAddAnother}
          />
          <BottomButtons
            removeLastScannedBarcode={removeLastScannedBarcode}
            scannerActive={scannerActive}
            setScannerActive={setScannerActive}
            setTriggerAddAnother={setTriggerAddAnother}
          />
        </Box>
      </Header>
    </NSCItemScannerContext.Provider>
  );
};

export default React.memo(NSCItemScanner);

const styles = StyleSheet.create({
  parentBox: {
    flex: 1,
  },
});
